import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { usePosts } from "@/hooks/usePosts";
import { BRAND, GROUPS, KIDS_TAGLINE, sectionOf, type Section } from "@/lib/site";
import { Star } from "@/components/Doodles";
import { PostCard } from "@/components/site/PostCard";
import { ShopGrid, useListings } from "@/components/site/ShopGrid";
import { SuppliesBox } from "@/components/site/SuppliesBox";
import { AffiliateDisclosure } from "@/lib/affiliate";
import { useState } from "react";

function Header({ s, kids }: { s: Section; kids: boolean }) {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-10 sm:px-8 lg:pt-14" data-testid={`section-header-${s.slug}`}>
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="relative fade-up">
          {kids && <Star className="ink-doodle absolute -left-10 -top-8 hidden h-10 w-10 -rotate-12 xl:block" />}
          <p className="shop-eyebrow"><Link to="/blog" className="hover:text-[#1C1917]" data-testid="section-eyebrow-link">Gift ideas</Link>{!kids && <span data-testid="section-group"> · {GROUPS.find((g) => g.key === s.group)?.label}</span>}</p>
          <h1 className="shop-h1 mt-4 text-4xl sm:text-5xl lg:text-6xl" data-testid="section-title">{s.name}</h1>
          {kids && <p className="hand-note mt-3 -rotate-1 text-2xl sm:text-3xl" data-testid="kids-hand-note">made by little hands, with a little help</p>}
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#78716C] sm:text-lg" data-testid="section-intro">{s.intro}</p>
        </div>
        <img src={s.image} alt={s.name} className="aspect-[4/3] w-full rounded-2xl object-cover fade-up" />
      </div>
    </section>
  );
}


export default function SectionPage() {
  const { section = "" } = useParams();
  const s = sectionOf(section);
  const posts = usePosts();
  const items = useListings();
  const [picks, setPicks] = useState(0);
  usePageMeta(s ? `${s.name} | ${BRAND}` : BRAND, s ? (s.slug === "kids" ? `${KIDS_TAGLINE} ${s.blurb}` : s.blurb) : "");
  if (!s) return <Navigate to="/blog" replace />;
  const kids = s.slug === "kids";
  const mine = (posts || []).filter((p) => p.section === s.slug);
  const kits = (items || []).filter((i) => i.section === s.slug);

  return (
    <div className={kids ? "little-hands-theme" : ""} data-testid={`section-page-${s.slug}`}>
      <Header s={s} kids={kids} />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <h2 className="shop-h2 mt-20 text-2xl">Ideas and how-tos</h2>
        {posts && mine.length === 0 && <p className="mt-4 text-sm text-[#23324A]/65" data-testid="section-no-posts">New ideas for this section are coming soon.</p>}
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="section-posts">
          {mine.map((p, i) => <PostCard key={p.slug} post={p} index={i} />)}
        </div>
        {kits.length > 0 && (
          <section className="mt-20" data-testid="section-kits">
            <div className="mb-6 flex items-end justify-between">
              <h2 className="shop-h2 text-2xl">Printable kits</h2>
              <Link to={`/shop?section=${s.slug}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#23324A] hover:text-[#D9785C]">See all <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <ShopGrid items={kits.slice(0, 6)} />
          </section>
        )}
        <SuppliesBox section={s.slug} src={`gifts-${s.slug}`} onLoaded={setPicks} />
        {picks > 0 && <AffiliateDisclosure className="mt-4" />}
      </div>
    </div>
  );
}
